import type { NightmareType } from './nightmares.ts';
import { sec } from './units.ts';

/**
 * The Furrows of the shell (docs/LABERINTOS.md): a labyrinth the group works out over a campaign week, solo or
 * live. Its map is the group's: crossings seen, gates opened, camps pitched and chests taken stay for the week.
 * Every run starts clean (the fight is never kept) and ends by the rope, when the torch dies, or with the centre.
 */
export const MAZE = {
  minGroup: 5,
  minHeroes: 1,
  maxHeroes: 8,
  /** A campaign week: the labyrinth is laid out anew every this many days. */
  weekDays: 7,
  /** Crossings on a coarse hex grid of this radius; a corridor cell between neighbours. */
  radius: 8,
  /** The radius by the group's size when the week starts (players 1 to 8): a bigger group maps a bigger labyrinth. */
  radiusBy: [0, 8, 8, 10, 13, 13, 14, 14, 14],
  /** Extra corridors beyond the tree, as a share of the crossings (bp): loops and shortcuts. */
  loopsBp: 1000,
  /** Gates on extra corridors, shut until their lever (on the deeper side) is pulled. */
  gates: 3,
  /** Corridors that open and shut with the titan's breath: open this long, shut this long, each with its phase. */
  breaths: 6,
  breathOpen: sec(8),
  breathShut: sec(8),
  /** Camps along the way in to the centre, at these shares of it (bp). */
  camps: [2500, 5000, 7500],
  /** Chests in the farthest dead ends; one in `guardEvery` has an elite asleep by it. */
  chests: 6,
  guardEvery: 3,
  /** The open way into the hall leaves the tree this deep, as a share of its deepest crossing (bp): long, not endless. */
  hallDepthBp: 6500,
  /** Packs of nightmares: how many besides those on the way to the hall, and none this close (crossings) to the way in. */
  packs: 14,
  /** On the way to the hall, a pack every this many crossings (something happens as you go). */
  wayPackEvery: 5,
  packMinDepth: 6,
  /** A pack wakes when a hero comes within this many crossings by the corridors, and never strays farther than `leash` from home. */
  wakeCrossings: 1,
  leashCrossings: 3,
  /** Nightmares in a pack, by party size (heroes 1 to 8): a patrol, never a tide. Level scales their blows. */
  packSize: [0, 3, 4, 4, 5, 5, 5, 6, 6],
  /** Past four heroes a pack is mostly tougher, not bigger (BIG_PARTY; bp of their health, heroes 1 to 8). */
  packHpBp: [0, 10000, 10000, 10000, 10000, 12900, 15800, 15500, 17900],
  /** 9 % until the class balance (docs/BALANCE_CLASES.md): skills grow with the level; 15 % keeps furrows-4p at least as hard (bots 93.5 % against 94.5 %). */
  perLevelBp: 1500,
  inkBp: 2500,
  /** Ground nightmares that fight up close: nothing that flies over the walls or shoots across them. */
  bestiary: { goblin: 3, wolf: 3, skeleton: 3, slime: 1 } as Partial<Record<NightmareType, number>>,
  /** Chest guards: an inked elite of the bestiary, tougher than its kind by party size (heroes 1 to 4, past 4 BIG_PARTY; bp). */
  guardHpBp: [0, 12000, 18000, 26000, 36000],
  /** A crossing counts as seen with a standing hero this close (cells). */
  sightCells: 1,
  /** What a hero's torch lights around it (cells): heroes can be sent anywhere lit, as well as anywhere mapped. */
  torchCells: 3,
  /** Standing by a lever, a camp or a chest this long works it. */
  leverTicks: sec(3),
  campTicks: sec(4),
  chestTicks: sec(2),
  workRange: 900,
  /** Back by the rope: a hero this close to it can ask to climb; everyone standing has to be within `ropeGather`. */
  ropeRange: 1200,
  ropeGather: 2600,
  /** The torch: a run ends by itself after this long (everything kept). */
  torchTicks: sec(420),
  /** The prowler: hears a hero this many crossings away by the corridors; a share of its blows' worth of damage (by party size, heroes 1 to 8) scares it off. */
  prowler: {
    count: [0, 1, 1, 2, 2, 2, 3, 3, 3],
    hearCrossings: 5,
    staggerHp: [0, 500, 750, 1050, 1350, 1750, 2150, 2500, 2900],
    staggerTicks: sec(6),
    awayTicks: sec(30),
  },
  /** The old ogre asleep by the waystone in the centre: an inked elite ogre, tougher by party size (heroes 1 to 4, past 4 BIG_PARTY; its blows 1 to 8). */
  guardian: { kind: 'ogre' as NightmareType, hpBp: [0, 10000, 30000, 60000, 120000], dmgBp: [0, 4000, 5000, 6000, 7000, 7000, 7000, 7000, 7000] },
  /** Rewards: XP per new crossing for each hero in the run; a chest's dust to the storage and a chance (bp) at a piece for whoever opened it. */
  xpPerCrossing: 12,
  chest: { dust: 60, itemBp: 3500 },
  /** The week's chest, once per hero, for everyone who ran the Furrows that week, when the centre falls. */
  weekly: { xp: 900, dust: 150, relicBp: 3000 },
  /** The table of the Furrows: lantern posts with this many weeks won. */
  trophy: { postsAt: 3 },
};
